import React, { useState, useCallback } from 'react';
import {
    DropdownMenu,
    IconButton,
    Group,
    MeatballsFilled,
    Box,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { InstrumentationOptions } from 'types';
import { FavoriteButton } from 'components/FavoriteButton';
import { DownloadButton } from 'components/CommonUi/DownloadButton';
import { useTakenDownSample } from 'hooks/useTakenDown/useTakenDown';
import { useDownloadsPageMatch } from 'hooks/useDownloadsPageMatch/useDownloadsPageMatch';
import { AddSampleToCollectionTableButton } from 'components/CommonUi/AddSampleToCollectionButton/AddSampleToCollectionTableButton';
import AddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton';
import { CreatorButton } from '../../CommonUi/CreatorButton/CreatorButton';
import { SamplesTableActionsGroupProps } from './SamplesTableActionsGroup.types';
import { SamplesTableActionsList } from './SamplesTableActionsList/SamplesTableActionsList';

export const SamplesTableActionsGroup: React.FC<
    SamplesTableActionsGroupProps
> = ({
    page,
    sample,
    rowIndex,
    actionsToHide,
    isMd,
    isRowActive = false,
}): JSX.Element => {
    const [menuPackSlug, setMenuPackSlug] = useState('');
    const [popoverReplacement, setPopover] = useState<
        React.ReactElement | undefined
    >();
    const { isTakenDown } = useTakenDownSample(sample);
    const { isDownloadsPage } = useDownloadsPageMatch();
    const isTakenDownUIVisible = isTakenDown && isDownloadsPage;

    const reset = (): void => {
        setPopover(undefined);
    };

    const instrumentationOptions: InstrumentationOptions = {
        interactionSource: 'Sample List',
        ...(page && { page: page }),
        ...(rowIndex && { rowIndex: rowIndex }),
    };

    const handleTriggerClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
        event.stopPropagation();

        setMenuPackSlug(sample?.pack?.slug ?? '');
        reset();
    };

    const getButtonLabel = useCallback(
        (label = '') => {
            if (isTakenDownUIVisible) {
                return t`Feature is unavailable`;
            }

            return label;
        },
        [isTakenDownUIVisible],
    );

    return (
        <>
            {/* // This Box/React.Fragment is needed for isRowHover to prevent unwanted stop propagation on actionsButton */}
            <Box
                width="100%"
                height="100%"
                position="absolute"
                top="none"
                left="none"
            />
            <Group
                direction="horizontal"
                alignItems="center"
                gap={isMd ? '2xs' : 'xs'}
                justifyContent="flex-end"
                data-testid="samples-table-actions-group"
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <Group.Item zIndex={isTakenDownUIVisible ? 400 : 0}>
                    <CreatorButton
                        sample={sample}
                        variant="subtle"
                        isRounded
                        size="sm"
                        isDisabled={isTakenDownUIVisible}
                        label={getButtonLabel()}
                        isPlaceholderAllowed={!isRowActive}
                    />
                </Group.Item>
                {!isMd && (
                    <Group.Item>
                        <AddSampleToCollectionTableButton
                            isPlaceholderAllowed={!isRowActive}
                        >
                            <AddSampleToCollectionButton
                                samples={[sample]}
                                source={{ interaction: 'Samples table' }}
                                popoverPlacement="left-end"
                                isDisabled={isTakenDownUIVisible}
                                size="sm"
                                variant="subtle"
                                isRounded
                            />
                        </AddSampleToCollectionTableButton>
                    </Group.Item>
                )}
                {!isMd && (
                    <Group.Item>
                        <FavoriteButton
                            key={`reconciliation-isFavorited-${sample.isFavorited}`}
                            item={sample}
                            variant="subtle"
                            isRounded
                            size="sm"
                            isDisabled={isTakenDownUIVisible}
                            label={getButtonLabel()}
                            isPlaceholderAllowed={!isRowActive}
                        />
                    </Group.Item>
                )}
                <Group.Item>
                    <DownloadButton
                        item={sample}
                        variant="subtle"
                        isRounded
                        size="sm"
                        isPlaceholderAllowed={!isRowActive}
                    />
                </Group.Item>

                <Group.Item>
                    <DropdownMenu placement="bottom-end">
                        {({ isOpen, close }): JSX.Element => (
                            <>
                                <DropdownMenu.Trigger>
                                    <IconButton
                                        icon={MeatballsFilled}
                                        onClick={handleTriggerClick}
                                        aria-haspopup="true"
                                        aria-expanded={isOpen}
                                        aria-label={t`View more`}
                                        label={getButtonLabel(t`More`)}
                                        variant="subtle"
                                        isRounded
                                        size="sm"
                                        isDisabled={isTakenDownUIVisible}
                                    />
                                </DropdownMenu.Trigger>
                                <DropdownMenu.List py="none" px="none">
                                    {popoverReplacement || (
                                        <SamplesTableActionsList
                                            instrumentationOptions={
                                                instrumentationOptions
                                            }
                                            biggerThanMobile={!isMd}
                                            menuPackSlug={menuPackSlug}
                                            sample={sample}
                                            actionsToHide={actionsToHide}
                                            hide={close}
                                            setPopover={setPopover}
                                            page={page || 1}
                                        />
                                    )}
                                </DropdownMenu.List>
                            </>
                        )}
                    </DropdownMenu>
                </Group.Item>
            </Group>
        </>
    );
};
